import React, { useState } from 'react';
import { Box, IconButton, alpha } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { BuffOrDebuff, WarbandMemberDTO } from '../../types';
import { SharedMemberStatsAndHealth, SharedStatValue } from './SharedMemberStatsAndHealth';
import { getSplitStatValue } from '../../utils/splitStats';
import { useStatPopover } from '../../hooks/useStatPopover';
import { StatBreakdownPopover } from './StatBreakdownPopover';
import { ApplyLevelUpModal } from '../modals/ApplyLevelUpModal';

interface MemberStatsAndHealthProps {
  member: WarbandMemberDTO;
  editMode: boolean;
  levelUpBonuses?: { fight: number; shoot: number; will: number; health: number };
  unusedLevelUps?: number;
  onApplyLevelUp?: (stat: string) => Promise<void>;
  warbandBuffs: BuffOrDebuff[];
}

export const MemberStatsAndHealth: React.FC<MemberStatsAndHealthProps> = ({
  member,
  editMode,
  levelUpBonuses,
  unusedLevelUps,
  onApplyLevelUp,
  warbandBuffs,
}) => {
  const [applyLevelUpOpen, setApplyLevelUpOpen] = useState(false);
  const { statPopover, openStatPopover, closeStatPopover } = useStatPopover();
  const sharedStats: Record<keyof import('../../types').StargraveStatLine, SharedStatValue> = {
    move: { base: member.move, adjusted: getSplitStatValue(member, 'move', member.move, warbandBuffs), onClick: (event) => openStatPopover(event, member, 'move', 'Move', member.move, warbandBuffs) },
    fight: { base: member.fight + (levelUpBonuses?.fight ?? 0), adjusted: getSplitStatValue(member, 'fight', member.fight + (levelUpBonuses?.fight ?? 0), warbandBuffs), signed: true, onClick: (event) => openStatPopover(event, member, 'fight', 'Fight', member.fight + (levelUpBonuses?.fight ?? 0), warbandBuffs) },
    shoot: { base: member.shoot + (levelUpBonuses?.shoot ?? 0), adjusted: getSplitStatValue(member, 'shoot', member.shoot + (levelUpBonuses?.shoot ?? 0), warbandBuffs), signed: true, onClick: (event) => openStatPopover(event, member, 'shoot', 'Shoot', member.shoot + (levelUpBonuses?.shoot ?? 0), warbandBuffs) },
    armour: { base: member.armour, adjusted: getSplitStatValue(member, 'armour', member.armour, warbandBuffs), onClick: (event) => openStatPopover(event, member, 'armour', 'Armour', member.armour, warbandBuffs) },
    will: { base: member.will + (levelUpBonuses?.will ?? 0), adjusted: getSplitStatValue(member, 'will', member.will + (levelUpBonuses?.will ?? 0), warbandBuffs), signed: true, onClick: (event) => openStatPopover(event, member, 'will', 'Will', member.will + (levelUpBonuses?.will ?? 0), warbandBuffs) },
    health: { base: member.health + (levelUpBonuses?.health ?? 0), adjusted: getSplitStatValue(member, 'health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs), onClick: (event) => openStatPopover(event, member, 'health', 'Health', member.health + (levelUpBonuses?.health ?? 0), warbandBuffs) },
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
        <SharedMemberStatsAndHealth
          stats={sharedStats}
          actionContent={editMode && (unusedLevelUps ?? 0) > 0 ? (
          <IconButton
            size="small"
            onClick={() => setApplyLevelUpOpen(true)}
            aria-label={`Apply level up (${unusedLevelUps} available)`}
            title={`${unusedLevelUps} level up${unusedLevelUps !== 1 ? 's' : ''} available`}
            sx={{
              backgroundColor: alpha('#FFD700', 0.55),
              ':hover': {
                backgroundColor: alpha('#FFD700', 0.75),
              },
            }}
          >
            <FileUploadIcon fontSize="small" />
          </IconButton>
          ) : undefined}
        />
      </Box>
      <StatBreakdownPopover popover={statPopover} onClose={closeStatPopover} />
      {onApplyLevelUp && (
        <ApplyLevelUpModal
          open={applyLevelUpOpen}
          onClose={() => setApplyLevelUpOpen(false)}
          onApply={onApplyLevelUp}
        />
      )}
    </>
  );
};
